import { useState } from 'react';

const GOLD = '#f5b50a';
const MUTED_STAR = 'rgba(140,140,150,0.4)';

const RATING_LABELS = { 1: 'Pésimo', 2: 'Malo', 3: 'Regular', 4: 'Bueno', 5: 'Excelente' };

export function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('es-CO', { dateStyle: 'long', timeStyle: 'short' }); }
  catch { return ''; }
}

// Promedio + cantidad de calificaciones de una lista de comentarios.
export function ratingSummary(comments) {
  const rated = (comments || []).filter(c => c && c.rating);
  const count = rated.length;
  const avg = count ? rated.reduce((s, c) => s + Number(c.rating), 0) / count : 0;
  return { avg, count };
}

// Estrellas de solo lectura (doradas las llenas).
export function StarsDisplay({ value = 0, size = 15 }) {
  const v = Math.round(value);
  return (
    <span style={{ display: 'inline-flex', gap: '1px', fontSize: size, lineHeight: 1 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <span key={n} style={{ color: n <= v ? GOLD : MUTED_STAR }}>★</span>
      ))}
    </span>
  );
}

// Estrellas clicables: el cliente elige cuántas estrellas doradas dar.
function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div>
      <div style={{ display: 'flex', gap: '8px' }}>
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n} type="button"
            onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
            onClick={() => onChange(n)}
            aria-label={`${n} estrella${n > 1 ? 's' : ''}`}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '2.2rem', lineHeight: 1, color: n <= shown ? GOLD : MUTED_STAR, transition: 'color .12s, transform .12s', transform: n <= shown ? 'scale(1.05)' : 'none' }}
          >★</button>
        ))}
      </div>
      <p style={{ margin: '6px 0 0', fontSize: '0.82rem', color: shown ? GOLD : 'var(--pub-muted, #94a3b8)', fontWeight: shown ? 600 : 400 }}>
        {shown ? `${shown}/5 — ${RATING_LABELS[shown]}` : '1 estrella = pésimo · 5 estrellas = excelente'}
      </p>
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' };
const panel = { backgroundColor: 'var(--pub-surface, #1a1a2e)', color: 'var(--pub-text, #ffffff)', borderRadius: '14px', border: '1px solid var(--pub-border, rgba(56,189,248,0.3))', padding: '1.4rem', width: '100%', maxWidth: '460px', maxHeight: '88vh', overflow: 'auto' };
const head = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '1rem' };
const closeBtn = { background: 'none', border: 'none', color: 'var(--pub-muted, #94a3b8)', fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1, flexShrink: 0 };
const label = { display: 'block', color: 'var(--pub-muted, #94a3b8)', fontSize: '0.82rem', marginBottom: '0.3rem' };
const input = { width: '100%', padding: '10px 12px', backgroundColor: 'var(--pub-bg, #0f0f0f)', border: '1px solid var(--pub-border, rgba(56,189,248,0.3))', borderRadius: '8px', color: 'var(--pub-text, #ffffff)', fontSize: '0.92rem', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' };
const primaryBtn = { width: '100%', padding: '11px 20px', backgroundColor: 'var(--pub-accent, #38bdf8)', color: 'var(--pub-bg, #0f0f0f)', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' };
const title = { color: 'var(--pub-accent, #38bdf8)', margin: 0, fontSize: '1.1rem' };
const thumbBtn = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '14px 22px', backgroundColor: 'var(--pub-bg, #0f0f0f)', border: '1px solid var(--pub-border, rgba(56,189,248,0.3))', borderRadius: '12px', color: 'var(--pub-text, #ffffff)', cursor: 'pointer', minWidth: '110px', fontFamily: 'inherit' };

// CONFIGURACIÓN por tipo de reseña.
function buildConfig(mode, slug, target) {
  if (mode === 'product') return {
    heading: 'Calificar producto', subject: target.title,
    numberLabel: 'Número de tu pedido', numberHelp: 'Lo recibiste al finalizar tu compra. Solo puedes calificar el producto que compraste con ese número.',
    verifyUrl: `/api/tienda/${slug}/review/verify`, submitUrl: `/api/tienda/${slug}/review`,
    idKey: 'order_id', targetKey: 'product_pk', targetVal: target.pk,
    withRating: true, needsNumber: true,
  };
  if (mode === 'service') return {
    heading: 'Calificar servicio', subject: target.name,
    numberLabel: 'Número de tu cita', numberHelp: 'Lo recibiste al agendar o pagar tu cita. Solo puedes calificar el servicio que agendaste con ese número.',
    verifyUrl: `/api/cita/${slug}/review/verify`, submitUrl: `/api/cita/${slug}/review`,
    idKey: 'appointment_id', targetKey: 'service_id', targetVal: target.id,
    withRating: true, needsNumber: true,
  };
  return { // news
    heading: 'Dejar un comentario', subject: target.title,
    submitUrl: `/api/public-news/${slug}/comment`,
    voteUrl: `/api/public-news/${slug}/vote`,
    targetKey: 'news_id', targetVal: target.id,
    withRating: false, needsNumber: false, withVote: true,
  };
}

// Modal para calificar/comentar. mode: 'product' | 'service' | 'news'.
export function ReviewModal({ mode, slug, target, onClose, onSubmitted }) {
  const cfg = buildConfig(mode, slug, target);
  const [step, setStep] = useState(cfg.needsNumber ? 1 : 2);
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [showVote, setShowVote] = useState(false);

  const verify = async () => {
    if (!number.trim()) { setError('Ingresa el número.'); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetch(cfg.verifyUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [cfg.idKey]: number.trim(), [cfg.targetKey]: cfg.targetVal }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.ok) { setName(d.name || ''); setStep(2); }
      else setError(d.message || 'No pudimos validar el número.');
    } catch { setError('Error de conexión. Intenta de nuevo.'); }
    finally { setLoading(false); }
  };

  const submit = async () => {
    if (!name.trim()) { setError('Escribe tu nombre.'); return; }
    if (cfg.withRating && !rating) { setError('Selecciona cuántas estrellas le das.'); return; }
    if (!cfg.withRating && !comment.trim()) { setError('Escribe un comentario.'); return; }
    setError(''); setLoading(true);
    const body = { [cfg.targetKey]: cfg.targetVal, name: name.trim(), comment: comment.trim() };
    if (cfg.needsNumber) body[cfg.idKey] = number.trim();
    if (cfg.withRating) body.rating = rating;
    try {
      const res = await fetch(cfg.submitUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.ok) {
        onSubmitted && onSubmitted();
        // En noticias, tras comentar preguntamos si le gusta (👍/👎); en el resto
        // mostramos el agradecimiento directo.
        if (cfg.withVote) setShowVote(true); else setDone(true);
      } else setError(d.message || 'No se pudo guardar.');
    } catch { setError('Error de conexión. Intenta de nuevo.'); }
    finally { setLoading(false); }
  };

  // Reacción a la noticia justo después de comentar. Si falla no bloqueamos:
  // el comentario ya quedó guardado.
  const vote = async (value) => {
    setError(''); setLoading(true);
    try {
      await fetch(cfg.voteUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ news_id: cfg.targetVal, vote: value }),
      });
      onSubmitted && onSubmitted();
    } catch { /* el comentario ya se guardó; ignoramos el error de la reacción */ }
    finally { setLoading(false); setDone(true); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={head}>
          <h3 style={title}>{cfg.heading}</h3>
          <button onClick={onClose} style={closeBtn} aria-label="Cerrar">✕</button>
        </div>
        {cfg.subject && <p style={{ margin: '0 0 1rem', fontWeight: 600, fontSize: '0.95rem' }}>{cfg.subject}</p>}

        {done ? (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div style={{ fontSize: '2.6rem', marginBottom: '0.5rem' }}>🎉</div>
            <p style={{ margin: '0 0 1.2rem' }}>¡Gracias! Tu {cfg.withRating ? 'calificación' : 'comentario'} se publicó.</p>
            <button onClick={onClose} style={primaryBtn}>Listo</button>
          </div>
        ) : showVote ? (
          <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
            <p style={{ margin: '0 0 0.3rem', fontWeight: 700, fontSize: '1rem' }}>¡Comentario publicado! 🎉</p>
            <p style={{ margin: '0 0 1.3rem', color: 'var(--pub-muted, #94a3b8)', fontSize: '0.92rem' }}>¿Te gusta esta noticia?</p>
            <div style={{ display: 'flex', gap: '0.9rem', justifyContent: 'center' }}>
              <button onClick={() => vote('up')} disabled={loading} style={thumbBtn}>
                <span style={{ fontSize: '1.8rem', lineHeight: 1 }}>👍</span>
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Me gusta</span>
              </button>
              <button onClick={() => vote('down')} disabled={loading} style={thumbBtn}>
                <span style={{ fontSize: '1.8rem', lineHeight: 1 }}>👎</span>
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>No me gusta</span>
              </button>
            </div>
            <button onClick={() => setDone(true)} disabled={loading} style={{ background: 'none', border: 'none', color: 'var(--pub-muted, #94a3b8)', cursor: 'pointer', marginTop: '1.2rem', fontSize: '0.85rem' }}>
              Ahora no
            </button>
          </div>
        ) : step === 1 ? (
          <>
            <label style={label}>{cfg.numberLabel}</label>
            <input
              value={number} onChange={e => setNumber(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') verify(); }}
              placeholder="Ej: 304" style={input} inputMode="numeric"
            />
            <p style={{ color: 'var(--pub-muted, #94a3b8)', fontSize: '0.78rem', margin: '0.5rem 0 1rem', lineHeight: 1.5 }}>{cfg.numberHelp}</p>
            {error && <p style={{ color: '#f87171', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{error}</p>}
            <button onClick={verify} disabled={loading} style={{ ...primaryBtn, opacity: loading ? 0.6 : 1 }}>
              {loading ? 'Validando...' : 'Continuar'}
            </button>
          </>
        ) : (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <label style={label}>Tu nombre</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Nombre" style={input} />
            </div>
            {cfg.withRating && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={label}>Tu calificación</label>
                <StarPicker value={rating} onChange={setRating} />
              </div>
            )}
            <div style={{ marginBottom: '1rem' }}>
              <label style={label}>Comentario{cfg.withRating ? ' (opcional)' : ''}</label>
              <textarea value={comment} onChange={e => setComment(e.target.value)} rows={4} placeholder="Cuéntanos tu experiencia..." style={{ ...input, resize: 'vertical' }} />
            </div>
            {error && <p style={{ color: '#f87171', fontSize: '0.85rem', marginBottom: '0.8rem' }}>{error}</p>}
            <button onClick={submit} disabled={loading} style={{ ...primaryBtn, opacity: loading ? 0.6 : 1 }}>
              {loading ? 'Enviando...' : 'Publicar'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Modal que lista los comentarios/calificaciones de una tarjeta.
export function CommentsModal({ withRating = true, heading, comments = [], onClose }) {
  const { avg, count } = ratingSummary(comments);
  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={head}>
          <h3 style={title}>{heading || 'Opiniones'}</h3>
          <button onClick={onClose} style={closeBtn} aria-label="Cerrar">✕</button>
        </div>

        {withRating && count > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
            <span style={{ fontSize: '1.6rem', fontWeight: 800, color: GOLD }}>{avg.toFixed(1)}</span>
            <div>
              <StarsDisplay value={avg} size={16} />
              <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'var(--pub-muted, #94a3b8)' }}>{count} calificación{count !== 1 ? 'es' : ''}</p>
            </div>
          </div>
        )}

        {comments.length === 0 ? (
          <p style={{ color: 'var(--pub-muted, #94a3b8)', textAlign: 'center', padding: '1.5rem 0' }}>
            Aún no hay {withRating ? 'opiniones' : 'comentarios'}. ¡Sé el primero!
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
            {comments.map((c, i) => (
              <div key={i} style={{ backgroundColor: 'var(--pub-bg, #0f0f0f)', border: '1px solid var(--pub-border, rgba(56,189,248,0.3))', borderRadius: '10px', padding: '0.8rem 0.9rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: '0.9rem' }}>{c.name || 'Anónimo'}</strong>
                  {withRating && c.rating ? <StarsDisplay value={c.rating} size={14} /> : null}
                </div>
                {c.created_at && <div style={{ color: 'var(--pub-muted, #94a3b8)', fontSize: '0.72rem', marginTop: '2px' }}>{fmtDate(c.created_at)}</div>}
                {c.comment && <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.comment}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
